"use client";

import { SessionProvider } from "next-auth/react";

import { HeroUIProvider } from "@heroui/system";
import { ToastProvider } from "@heroui/toast";

import { ObservabilityLayoutIntegration } from "@/lib/observability/layout-integration";

/**
 * Global application providers
 * - SessionProvider: Makes session available to all client components
 * - HeroUIProvider: Provides UI components with accessibility enhancements
 * - ToastProvider: Provides toast notifications
 *
 * @see https://authjs.dev/getting-started/session-management/get-session
 */

interface HeroUIError {
  target?: EventTarget | null;
  nativeEvent?: Event & { target?: EventTarget | null };
  type?: string;
}

function getElementTag(el: EventTarget | null | undefined): string {
  if (!el) return "";

  return (el as HTMLElement).tagName ?? "";
}

function getElementType(el: EventTarget | null | undefined): string {
  if (!el) return "";

  return (el as HTMLElement & { elementType?: string }).elementType ?? "";
}

function isHeroUIImageError(error: HeroUIError): boolean {
  const target = error.target;
  const nativeTarget = error.nativeEvent ? error.nativeEvent.target : null;

  if (getElementTag(target) === "IMG") return true;
  if (getElementTag(nativeTarget) === "IMG") return true;

  return error.type === "error" && getElementType(target) === "img";
}

function handleHeroUIError(error: HeroUIError): void {
  if (isHeroUIImageError(error)) return;
  console.error("HeroUI Accessibility Error:", error);
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      <HeroUIProvider
        disableAnimation={false}
        disableRipple={false}
        onError={handleHeroUIError}
      >
        <ToastProvider maxVisibleToasts={3} placement="bottom-right" />
        <ObservabilityLayoutIntegration>
          {children}
        </ObservabilityLayoutIntegration>
      </HeroUIProvider>
    </SessionProvider>
  );
}
